import * as eventable from "../../utils/eventable";
import { isEventable } from "../../utils/helpers";

export interface ISelectedRegionConfig {
	className?: string;
	render?: (startPoint: IPoint, endPoint: IPoint) => HTMLElement;
	viewPort?: HTMLElement;
	useRequestAnimationFrame: boolean;
	callback?: (startPoint: IPoint, endPoint: IPoint, startDate: Date, endDate: Date, tasksByDate: any[], tasksByIndex: any[]) => void;
	singleRow: boolean;
}

interface ICoordinates {
	left: number;
	top: number;
}

export interface IPoint {
	absolute: ICoordinates;
	relative: ICoordinates;
}


function _countSize(start: number, end: number) {
	const result = start - end;
	if (result >= 0) {
		return result;
	}
	return -result;
}

export class SelectedRegion {
	private _viewPort: HTMLElement & eventable;
	private _el: HTMLElement = document.createElement("div");
	private _callback: (startPoint: IPoint, endPoint: IPoint, startDate: Date, endDate: Date, tasksByDate: any[], tasksByIndex: any[]) => void;
	private _startPoint: IPoint;
	private _endPoint: IPoint;
	private _positionPoint: IPoint;
	private _useRequestAnimationFrame: boolean;
	private _startDate: Date;
	private _endDate: Date;
	private _singleRow: boolean;
	private _gantt: any;
	constructor(config: ISelectedRegionConfig, gantt: any) {
		this._gantt = gantt;
		this._viewPort = config.viewPort;
		this._el.classList.add(config.className);
		if (typeof config.callback === "function") {
			this._callback = config.callback;
		}
		if (typeof config.render === "function") {
			this.render = () => {
				this._el = config.render(this._startPoint, this._endPoint);
				if (config.className !== "") {
					this._el.classList.add(config.className);
				}
				this.draw();
			};
		}
		if (!isEventable(this._viewPort)) {
			eventable(this._viewPort);
		}
		this._singleRow = config.singleRow;
		this._useRequestAnimationFrame = config.useRequestAnimationFrame;
	}

	setStyles() {
		const gantt = this._gantt;
		if (this._singleRow) {
			const height = gantt.config.row_height;
			this._el.style.height = height + "px";
			this._el.style.top = (Math.ceil(this._positionPoint.relative.top / height) - 1) * height + "px";
		} else {
			this._el.style.height = _countSize(this._endPoint.relative.top, this._startPoint.relative.top) + "px";
			this._el.style.top = this._positionPoint.relative.top + "px";
		}
		this._el.style.width = _countSize(this._endPoint.relative.left, this._startPoint.relative.left) + "px";
		this._el.style.left = this._positionPoint.relative.left + "px";
	}

	render() {
		this.setStyles();
		this.draw();
	}

	draw() {
		if (this._useRequestAnimationFrame) {
			return requestAnimationFrame(() => {
				this._viewPort.appendChild(this.getElement());
			});
		} else {
			this._viewPort.appendChild(this.getElement());
		}
	}

	clear() {
		if (this._useRequestAnimationFrame) {
			return requestAnimationFrame(() => {
				if (!this._el.parentNode) {
					return;
				}
				this._viewPort.removeChild(this._el);
			});
		} else {
			if (!this._el.parentNode) {
				return;
			}
			this._viewPort.removeChild(this._el);
		}
	}

	getElement() {
		return this._el;
	}

	getViewPort() {
		return this._viewPort;
	}

	setStart(startPoint: IPoint) {
		const gantt = this._gantt;
		this._startPoint = startPoint;
		this._startDate = gantt.dateFromPos(this._startPoint.relative.left);
		this._viewPort.callEvent("onBeforeDrag", [this._startPoint]);
	}

	setEnd(endPoint: IPoint) {
		const gantt = this._gantt;
		this._endPoint = endPoint;
		if (this._singleRow) {
			const height = gantt.config.row_height;
			this._endPoint.relative.top = (Math.ceil(this._startPoint.relative.top / height)) * height;
		}
		this._endDate = gantt.dateFromPos(this._endPoint.relative.left);
		if (this._startPoint.relative.left > this._endPoint.relative.left) {
			this._positionPoint = {
				relative: { left: this._endPoint.relative.left, top: this._positionPoint.relative.top },
				absolute: { left: this._endPoint.absolute.left, top: this._positionPoint.absolute.top }
			};
		}
		if (this._startPoint.relative.top > this._endPoint.relative.top) {
			this._positionPoint = {
				relative: { left: this._positionPoint.relative.left, top: this._endPoint.relative.top },
				absolute: { left: this._positionPoint.absolute.left, top: this._endPoint.absolute.top }
			};
		}


		this._viewPort.callEvent("onDrag", [this._startPoint, this._endPoint]);
	}

	setPosition(positionPoint: IPoint) {
		this._positionPoint = positionPoint;
	}

	dragEnd(endPoint: IPoint) {
		const gantt = this._gantt;
		if(endPoint.relative.left < 0){
			endPoint.relative.left = 0;
		}
		this._viewPort.callEvent("onBeforeDragEnd", [this._startPoint, endPoint]);
		this.setEnd(endPoint);
		if (this._startDate.valueOf() > this._endDate.valueOf()) {
			[ this._startDate, this._endDate ] = [ this._endDate, this._startDate ];
		}
		this.clear();
		const tasksByTime = gantt.getTaskByTime(this._startDate, this._endDate);
		const tasksByIndex = this._getTasksByTop(this._startPoint.relative.top, this._endPoint.relative.top);

		this._viewPort.callEvent("onDragEnd", [this._startPoint, this._endPoint]);
		if (this._callback) {
			this._callback(this._startPoint, this._endPoint, this._startDate, this._endDate, tasksByTime, tasksByIndex);
		}
	}

	getInBounds() {
		return this._singleRow;
	}

	private _getTasksByTop(start: number, end:number) {
		const gantt = this._gantt;
		let startValue = start;
		let endValue = end;
		if (start > end) {
			startValue = end;
			endValue = start;
		}
		const height = gantt.config.row_height;
		const startIndex = Math.ceil(startValue / height) - 1;
		const endIndex = Math.ceil(endValue / height) - 1;
		const result = [];
		for (let i = startIndex; i <= endIndex; i++) {
			const task = gantt.getTaskByIndex(i);
			if (task) {
				result.push(gantt.getTaskByIndex(i));
			}
		}
		return result;
	}
}